import { RefObject, useCallback, useEffect, useState } from 'react';

interface UseHandleDragProps {
  onDrag: (position: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  containerRef: RefObject<HTMLDivElement | null>;
  getValueFromEvent?: (e: MouseEvent | TouchEvent, rect: DOMRect) => number;
}

export const useHandleDrag = ({
  onDrag,
  onDragStart,
  onDragEnd,
  containerRef,
  getValueFromEvent,
}: UseHandleDragProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || !containerRef?.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const position = getValueFromEvent
        ? getValueFromEvent(e, rect)
        : ((clientX - rect.left) / rect.width) * 100;

      onDrag(position);
    },
    [isDragging, onDrag, getValueFromEvent],
  );

  const handleEnd = useCallback(() => {
    setIsDragging(false);
    onDragEnd?.();
  }, [onDragEnd]);

  const handleStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      setIsDragging(true);
      onDragStart?.();
      e.stopPropagation();
    },
    [onDragStart],
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, handleMove, handleEnd]);

  return {
    isDragging,
    handleMouseDown: handleStart,
    handleTouchStart: handleStart,
  };
};
